import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Playbook } from '@/api/entities';
import { AccountFinal } from '@/api/entities';
import { PersonFinal } from '@/api/entities';
import { User } from '@/api/entities';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Play, Zap, TrendingUp, Users, Target, Plus, Edit, Trash2, Loader2, CalendarDays, MapPin, ArrowUpCircle, Star, Sparkles, Copy } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  EVENT_PLAYBOOK_KEYS,
  EVENT_PLAYBOOK_DISPLAY,
  getEventPlaybookDisplay,
  getEventPlaybookKey,
  getEventConfirmedAttendeeCount,
  isQuickConnectPlaybookKey,
  isSessionInvitePlaybookKey,
  hasEventSessionDetails,
  isEventPlaybookPreviewBlocked,
  getEligibilityBlockMessage,
} from '@/lib/eventPlaybookDisplay';
import EventCustomPlaybookStatusBanner from '@/components/events/EventCustomPlaybookStatusBanner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { createPageUrl, normalizePlaybookKey } from '@/utils';
import { resolveEffectiveTenant, isAccountExcludedFromPlaybooks, isEvasysFamilyTenant } from '@/lib/tenantUtils';
import { applyTenantOverrides } from '@/components/utils/tenantOverrides';
import {
  isEvasysAccountEligibleForExamIntegrityPlaybook,
  isEvasysAccountEligibleForSysGovernancePlaybook,
} from '@/lib/accountTargetProducts';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { cloneDiyPlaybook } from '@/lib/cloneDiyPlaybook';

/** Canonical playbook key for API calls and state. */
function getPlaybookKey(playbook) {
  if (!playbook) return '';
  const raw = String(playbook.key || playbook.playbook_id || playbook.id || '').trim();
  if (raw.toLowerCase().startsWith('event::')) return raw;
  return normalizePlaybookKey(raw);
}

/** Keep synthetic event keys (event::<uuid>) intact for event preview routing. */
function getRawPlaybookKey(playbook) {
  if (!playbook) return '';
  return String(playbook.playbook_key || playbook.playbook_id || playbook.key || playbook.id || '').trim();
}

function getPlaybookDisplayName(playbook) {
  if (!playbook) return 'Untitled Playbook';
  return (
    playbook.playbook_name ||
    playbook.name ||
    playbook.display_name ||
    playbook.event_name ||
    (getRawPlaybookKey(playbook).toLowerCase().startsWith('event::') ? 'Custom Event Playbook' : 'Untitled Playbook')
  );
}

function sortAccountsByFitScoreDesc(accounts) {
  const getFitScore = (account) => {
    const raw =
      account?.account_fit_score ??
      account?.fit_score ??
      account?.accountFitScore;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };
  return [...(accounts || [])].sort((a, b) => {
    const aScore = getFitScore(a);
    const bScore = getFitScore(b);
    const aValid = Number.isFinite(aScore);
    const bValid = Number.isFinite(bScore);
    if (aValid && bValid && aScore !== bScore) return bScore - aScore;
    if (aValid && !bValid) return -1;
    if (!aValid && bValid) return 1;
    return 0;
  });
}
import AccountSelector from '../components/AccountSelector';
import { toast } from 'sonner';
import { useTranslation } from '@/components/utils/translations';

const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'https://api.neogtm.com'
  : 'https://api.neogtm.com';

const UNIVERSITY_PLAYBOOK_IDS = [
  'new_budget_strategic_initiative_v1',
  'leadership_change_v1',
  'champion_led_introduction_v1',
  'incumbent_displacement_v1',
  'strategic_alignment_v1'
];

/** API may return snake_case; UI expects name + goalDescription for DIY cards. */
function normalizeDiyPlaybookRow(p) {
  if (!p) return null;
  const id = p.id || p.playbook_id;
  const ownerRaw =
    p.owner_user_id ?? p.user_id ?? p.userId ?? p.created_by_user_id ?? null;
  return {
    ...p,
    id,
    playbook_id: p.playbook_id || id,
    name: p.name || p.playbook_name || '',
    goalDescription: p.goalDescription ?? p.goal_description ?? '',
    owner_user_id: ownerRaw != null ? String(ownerRaw).trim() : undefined,
  };
}

function diyPlaybookEditorUserId(user) {
  return String(user?.id ?? user?.user_id ?? '').trim();
}

function diyPlaybookOwnerUserId(playbook) {
  const raw =
    playbook?.owner_user_id ??
    playbook?.user_id ??
    playbook?.userId ??
    playbook?.created_by_user_id;
  return raw != null ? String(raw).trim() : '';
}

/** Legacy rows without owner_user_id are treated as editable by the current user. */
function isDiyPlaybookOwnedByUser(playbook, user) {
  const ownerId = diyPlaybookOwnerUserId(playbook);
  const editorId = diyPlaybookEditorUserId(user);
  if (!editorId) return false;
  if (!ownerId) return true;
  return ownerId === editorId;
}

export default function Playbooks({ currentTenant: currentTenantProp }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const currentTenant = currentTenantProp || contextTenant;
  const [playbooks, setPlaybooks] = useState([]);
  const [diyPlaybooks, setDiyPlaybooks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFactor, setFilterFactor] = useState('all');
  const [selectedPlaybook, setSelectedPlaybook] = useState(null);
  const [showAccountSelector, setShowAccountSelector] = useState(false);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [hideFullyEnrolledAccounts, setHideFullyEnrolledAccounts] = useState(true);
  const [accountCountryFilter, setAccountCountryFilter] = useState('all');
  const [isFetchingAccounts, setIsFetchingAccounts] = useState(false);
  const [showEnrolledMap, setShowEnrolledMap] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');

  const isVisibleForPlaybooks = (account, tenantId) => {
    const withOverrides = applyTenantOverrides(account, tenantId);
    return withOverrides?.is_visible !== false && !isAccountExcludedFromPlaybooks(tenantId, withOverrides);
  };
  const effectiveTenant = resolveEffectiveTenant(
    contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
    currentTenant,
    contextUser,
    'instructure'
  );
  /** Must match effectiveTenant so admin-active-tenant (e.g. learnwise) loads user_playbooks for that tenant. */
  const tenantIdForDiy = effectiveTenant;

  useEffect(() => {
    loadData();
    fetchUpcomingEvents();
  }, [currentTenant, contextUser, effectiveTenant]);

  const fetchUpcomingEvents = async () => {
    if (!contextUser?.id) return;
    try {
      const tenantId = effectiveTenant;
      
      const response = await fetch(
        `${API_BASE_URL}/api/settings/events?tenant_id=${tenantId}&scope=tenant`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        const allEvents = Array.isArray(data.events) ? data.events : [];

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const isEventCurrentOrUpcoming = (e) => {
          const rawDate = e.end_date || e.start_date;
          if (!rawDate) return false;
          const eventDate = new Date(rawDate);
          if (Number.isNaN(eventDate.getTime())) return false;
          eventDate.setHours(0, 0, 0, 0);
          return eventDate >= today;
        };

        const selectableEvents = allEvents.filter(isEventCurrentOrUpcoming);
        setUpcomingEvents(selectableEvents);
        setSelectedEventId((prev) => {
          const prevKey = String(prev || '').trim();
          if (
            prevKey &&
            selectableEvents.some((e) => String(e.id || e.event_name) === prevKey)
          ) {
            return prev;
          }
          return selectableEvents.length > 0
            ? selectableEvents[0].id || selectableEvents[0].event_name
            : '';
        });
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const loadData = async () => {
    if (!contextUser) return;
    setIsLoading(true);
    try {
      // Evasys: load playbooks from API, not hardcoded config
      if (isEvasysFamilyTenant(effectiveTenant)) {
        const res = await fetch(
          `${API_BASE_URL}/api/playbooks/available?tenant_id=${encodeURIComponent(effectiveTenant)}`,
          { credentials: 'include', headers: { 'X-Tenant-ID': effectiveTenant } }
        );
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data)
            ? data
            : (data.playbooks || data.items || data.data || data.rows || data.records || []);
          const evasysPlaybooks = list.map(p => {
            const goal = p.goal ?? p.value_narrative ?? p.value_description ?? p.goal_description ?? '';
            return {
              ...p,
              key: normalizePlaybookKey(p.key || p.playbook_id || p.id),
              playbook_id: normalizePlaybookKey(p.playbook_id || p.key || p.id),
              playbook_name: p.playbook_name ?? p.name,
              goal,
              when_to_use: p.when_to_use ?? p.when_to_use_description ?? '',
              factor: p.factor ?? p.archetype ?? p.archetype_name ?? ''
            };
          });
          setPlaybooks(
            evasysPlaybooks.filter(
              (p) => {
                const key = normalizePlaybookKey(p.playbook_id || p.key);
                return key !== 'evasys_leadership_change_v2' && key !== 'evasys_incumbent_displacement_v2';
              }
            )
          );
          console.log('[Playbooks] standard playbooks count (evasys)', evasysPlaybooks.length, {
            effectiveTenant,
            tenantIdForDiy,
          });
          // Load DIY playbooks for evasys (tenant = effectiveTenant, includes admin-active-tenant)
          try {
            const diyRes = await fetch(
              `${API_BASE_URL}/api/diy-playbooks?tenant_id=${encodeURIComponent(tenantIdForDiy)}&user_id=${encodeURIComponent(contextUser?.id || '')}`,
              { credentials: 'include', headers: { 'X-Tenant-ID': tenantIdForDiy } }
            );
            let apiPlaybooks = [];
            if (diyRes.ok) {
              const diyData = await diyRes.json();
              apiPlaybooks = (Array.isArray(diyData) ? diyData : diyData.playbooks || [])
                .filter((p) => String(p?.tenant_id || '').toLowerCase() === String(tenantIdForDiy).toLowerCase())
                .map(normalizeDiyPlaybookRow)
                .filter(Boolean);
            } else {
              console.warn('[Playbooks] DIY API non-OK (evasys)', diyRes.status);
            }
            const localKey = `diy-playbooks-${tenantIdForDiy}`;
            let localPlaybooks = [];
            try {
              const stored = localStorage.getItem(localKey);
              localPlaybooks = stored ? JSON.parse(stored) : [];
            } catch (e) {
              localPlaybooks = [];
            }
            const apiIds = new Set(apiPlaybooks.map((p) => p.id || p.playbook_id));
            const merged = [
              ...apiPlaybooks,
              ...localPlaybooks.map(normalizeDiyPlaybookRow).filter(Boolean).filter((p) => !apiIds.has(p.id)),
            ];
            console.log('[Playbooks] DIY playbooks count (evasys)', merged.length, {
              apiCount: apiPlaybooks.length,
              recentSample: merged.slice(0, 8).map((p) => ({ id: p.id, name: p.name })),
            });
            setDiyPlaybooks(merged);
          } catch (e) {
            console.error('DIY playbooks load:', e);
            setDiyPlaybooks([]);
          }
          setIsLoading(false);
          return;
        }
        console.warn('Evasys playbooks API failed, falling back to empty list');
        setPlaybooks([]);
        setDiyPlaybooks([]);
        setIsLoading(false);
        return;
      }

      // Learnwise: DIY + event playbooks only (standard tenant playbooks hidden)
      if (effectiveTenant === 'learnwise') {
        const res = await fetch(
          `${API_BASE_URL}/api/playbooks/available?tenant_id=learnwise`,
          { credentials: 'include', headers: { 'X-Tenant-ID': 'learnwise' } }
        );
        let eventPlaybooks = [];
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data)
            ? data
            : (data.playbooks || data.items || data.data || data.rows || data.records || []);
          eventPlaybooks = (list || [])
            .filter((p) => EVENT_PLAYBOOK_KEYS.includes(normalizePlaybookKey(p.playbook_id || p.key)))
            .map((p) => {
              const goal = p.goal ?? p.value_narrative ?? p.value_description ?? p.goal_description ?? '';
              return {
                ...p,
                key: normalizePlaybookKey(p.key || p.playbook_id || p.id),
                playbook_id: normalizePlaybookKey(p.playbook_id || p.key || p.id),
                playbook_name: p.playbook_name ?? p.name,
                tenant_id: 'global',
                goal,
                when_to_use: p.when_to_use ?? p.when_to_use_description ?? '',
                factor: p.factor ?? p.archetype ?? p.archetype_name ?? '',
              };
            });
        }
        setPlaybooks(eventPlaybooks);
        console.log('[Playbooks] event playbooks count (learnwise)', eventPlaybooks.length, {
          effectiveTenant,
          tenantIdForDiy,
        });
        // Load DIY playbooks for learnwise (tenant = effectiveTenant so admin-active-tenant matches DB)
        try {
          const diyRes = await fetch(
            `${API_BASE_URL}/api/diy-playbooks?tenant_id=${encodeURIComponent(tenantIdForDiy)}&user_id=${encodeURIComponent(contextUser?.id || '')}`,
            { credentials: 'include', headers: { 'X-Tenant-ID': tenantIdForDiy } }
          );
          let apiPlaybooks = [];
          if (diyRes.ok) {
            const diyData = await diyRes.json();
            apiPlaybooks = (Array.isArray(diyData) ? diyData : diyData.playbooks || [])
              .filter(
                (p) =>
                  String(p?.tenant_id || '').toLowerCase() === String(tenantIdForDiy).toLowerCase()
              )
              .map(normalizeDiyPlaybookRow)
              .filter(Boolean);
          } else {
            console.warn('[Playbooks] DIY API non-OK (learnwise)', diyRes.status);
          }
          const localKey = `diy-playbooks-${tenantIdForDiy}`;
          let localPlaybooks = [];
          try {
            const stored = localStorage.getItem(localKey);
            localPlaybooks = stored ? JSON.parse(stored) : [];
          } catch (e) {
            localPlaybooks = [];
          }
          const apiIds = new Set(apiPlaybooks.map((p) => p.id || p.playbook_id));
          const merged = [
            ...apiPlaybooks,
            ...localPlaybooks.map(normalizeDiyPlaybookRow).filter(Boolean).filter((p) => !apiIds.has(p.id)),
          ];
          console.log('[Playbooks] DIY playbooks count (learnwise)', merged.length, {
            apiCount: apiPlaybooks.length,
            recentSample: merged.slice(0, 8).map((p) => ({ id: p.id, name: p.name })),
          });
          setDiyPlaybooks(merged);
        } catch (e) {
          console.error('DIY playbooks load:', e);
          setDiyPlaybooks([]);
        }
        setIsLoading(false);
        return;
      }

      const playbookData = await Playbook.list();
      // [2026-05-04] Also pull global event playbooks via /api/playbooks/available.
      // Reason: base44's entity API tenant-filters Playbook.list() so rows with
      // tenant_id='global' are never returned. The 4 default event playbooks
      // (Booth Meeting / Quick Connect / Session Invite / VIP Outreach) live in
      // the global tier, so they're invisible without this supplemental fetch.
      // The BE /available endpoint merges tenant rows + global event rows
      // server-side. We only take the event-key entries and merge them in;
      // the playbook_id allowlist (EVENT_PLAYBOOK_KEYS) is the trust gate.
      try {
        const me = await User.me();
        const tenantId = currentTenant || me?.tenant_id || 'instructure';
        const eventResp = await fetch(
          `${API_BASE_URL}/api/playbooks/available?tenant_id=${encodeURIComponent(tenantId)}`,
          { credentials: 'include' }
        );
        if (eventResp.ok) {
          const { playbooks: availablePlaybooks = [] } = await eventResp.json();
          const existingIds = new Set(playbookData.map((p) => p.playbook_id));
          for (const p of availablePlaybooks) {
            const pid = p.playbook_id || p.key;
            if (EVENT_PLAYBOOK_KEYS.includes(pid) && !existingIds.has(pid)) {
              playbookData.push({
                playbook_id: pid,
                playbook_name: p.playbook_name || p.name || p.display_name || pid,
                tenant_id: 'global',
                is_active: true,
              });
            }
          }
        }
      } catch (eventErr) {
        console.warn('Failed to fetch global event playbooks via /available:', eventErr?.message);
      }

    const SKILLWELL_PLAYBOOK_IDS = [
      'former_customers_v1',
      'cfo_value_risk_v1',
      'skills_ld_hr_v1',
      'technology_transformation_v1',
      'event_alignment_v1'
    ];

    const ALLOWED_GLOBAL_PLAYBOOK_IDS = [
      // Event playbooks
      'event_booth_meeting_v1',
      'event_session_invite_v1',
      'event_vip_outreach_v1',
      'event_quick_connect_v1',
      // Standard playbooks for university tenants
      'new_budget_initiative_v1',
      'leadership_change_v1',
      'finding_champions_v1',
      'incumbent_displacement_v1',
      'strategic_priority_v1'
    ];

    const INSTRUCTURE_HIDDEN_PLAYBOOK_TITLES = ['Champion-led introduction', 'Incumbent Displacement Play'];

    // Determine which playbooks to show based on tenant
    const tenantPlaybooks = playbookData.filter(p => {
      // Check is_active instead of is_visible
      if (p.is_active === false) return false;

      // Instructure: hide specific playbooks by title
      if (effectiveTenant === 'instructure') {
        const title = (p.playbook_name || p.name || '').trim();
        if (INSTRUCTURE_HIDDEN_PLAYBOOK_TITLES.includes(title)) return false;
      }

      // Event playbooks: accept on hardcoded playbook_id allowlist regardless
      // of tenant_id. base44's entity API already isolates rows by the
      // authenticated user's tenant; the EVENT_PLAYBOOK_KEYS allowlist is
      // a separate trust gate. Previously this required `tenant_id === 'global'`,
      // but base44 strips global rows for non-admin users — so DB-backed
      // tenants (learnwise, evasys) need the supplemental /available fetch
      // AND this relaxed filter to surface event playbook tiles.
      if (EVENT_PLAYBOOK_KEYS.includes(normalizePlaybookKey(p.playbook_id))) {
        return true;
      }

      // SEBIT tenant: Show enrichment playbooks (es_ prefix)
      if (effectiveTenant === 'sebit') {
        return p.playbook_id?.startsWith('es_') && p.tenant_id === 'sebit';
      }

      // Skillwell tenant: Show skillwell-specific playbooks only
      if (effectiveTenant === 'skillwell') {
        return p.tenant_id === 'skillwell' || SKILLWELL_PLAYBOOK_IDS.includes(normalizePlaybookKey(p.playbook_id));
      }

      // Kaira Digital tenant: Show kairadigital-specific playbooks only
      if (effectiveTenant === 'kairadigital') {
        return p.tenant_id === 'kairadigital';
      }

      // Wayground tenant: Show wayground-specific playbooks only
      if (effectiveTenant === 'wayground') {
        return p.tenant_id === 'wayground';
      }

      // Faria tenant: Show faria-specific playbooks only
      if (effectiveTenant === 'faria') {
        return p.tenant_id === 'faria';
      }

      // EdQwest tenant: Show edqwest-specific playbooks only
      if (effectiveTenant === 'edqwest') {
        return p.tenant_id === 'edqwest';
      }

      // Calmedica tenant: DIY + event playbooks only (standard tenant playbooks hidden)
      if (effectiveTenant === 'calmedica') {
        return false;
      }

      // Evasys tenant: Show ONLY these 6 playbooks (no event playbooks, no global)
      const EVASYS_PLAYBOOK_IDS = [
        'evaexam_integrity_assessment_security_v2',
        'evasys_compliance_risk_v2',
        'evasys_governance_accreditation_ie_v2',
        'evasys_integration_workflow_modernisation_v2',
        'evasys_strategic_priorities_v2'
      ];
      if (isEvasysFamilyTenant(effectiveTenant)) {
        return isEvasysFamilyTenant(p.tenant_id) && EVASYS_PLAYBOOK_IDS.includes(normalizePlaybookKey(p.playbook_id));
      }

      // Learnwise tenant: DIY + event playbooks only (standard tenant playbooks hidden)
      if (effectiveTenant === 'learnwise') {
        return false;
      }

      // University tenants (instructure, smowl, clairelabs, prowise, plataformaa, diambra):
      // Show ONLY whitelisted global playbooks
      if (['instructure', 'smowl', 'clairelabs', 'prowise', 'plataformaa', 'diambra'].includes(effectiveTenant)) {
        // Show tenant-specific playbooks if they match
        if (p.tenant_id === effectiveTenant) {
          return true;
        }
        // Show ONLY whitelisted global playbooks
        if (p.tenant_id === 'global') {
          return ALLOWED_GLOBAL_PLAYBOOK_IDS.includes(normalizePlaybookKey(p.playbook_id));
        }
      }

      // Default: don't show
      return false;
    });
      
      setPlaybooks(tenantPlaybooks);
      console.log('[Playbooks] standard playbooks count (default branch)', tenantPlaybooks.length, {
        effectiveTenant,
        tenantIdForDiy,
      });

      // Load DIY playbooks (effectiveTenant aligns with admin-active-tenant)
      try {
        if (!tenantIdForDiy) {
          setDiyPlaybooks([]);
        } else {
          let apiPlaybooks = [];
          try {
            const response = await fetch(
              `${API_BASE_URL}/api/diy-playbooks?tenant_id=${encodeURIComponent(tenantIdForDiy)}&user_id=${encodeURIComponent(contextUser?.id || '')}`,
              { credentials: 'include', headers: { 'X-Tenant-ID': tenantIdForDiy } }
            );
            if (response.ok) {
              const diyData = await response.json();
              apiPlaybooks = (Array.isArray(diyData) ? diyData : diyData.playbooks || [])
                .filter(
                  (p) =>
                    String(p?.tenant_id || '').toLowerCase() === String(tenantIdForDiy).toLowerCase()
                )
                .map(normalizeDiyPlaybookRow)
                .filter(Boolean);
            } else {
              console.warn('[Playbooks] DIY API non-OK (default)', response.status);
            }
          } catch {
            // API failed, continue with local
          }

          const localKey = `diy-playbooks-${tenantIdForDiy}`;
          let localPlaybooks = [];
          try {
            const stored = localStorage.getItem(localKey);
            localPlaybooks = stored ? JSON.parse(stored) : [];
          } catch {
            localPlaybooks = [];
          }

          const apiIds = new Set(apiPlaybooks.map((p) => p.id || p.playbook_id));
          const merged = [
            ...apiPlaybooks,
            ...localPlaybooks.map(normalizeDiyPlaybookRow).filter(Boolean).filter((p) => !apiIds.has(p.id)),
          ];
          console.log('[Playbooks] DIY playbooks count (default branch)', merged.length, {
            apiCount: apiPlaybooks.length,
            recentSample: merged.slice(0, 8).map((p) => ({ id: p.id, name: p.name })),
          });
          setDiyPlaybooks(merged);
        }
      } catch (diyError) {
        console.error('Error loading DIY playbooks:', diyError);
      }
    } catch (error) {
      console.error('Error loading playbooks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEligibleAccounts = async (playbookKey, showEnrolled = false, includeAllEnrollmentStates = false) => {
    const emptyFetchResult = {
      accounts: [],
      /** True only when /eligible-accounts succeeded with an empty `accounts` array (before FE visibility filters). */
      backendEligibleAccountsListEmpty: false,
    };
    if (!contextUser?.id) return emptyFetchResult;
    try {
      const playbookKeyClean = normalizePlaybookKey(playbookKey);
      const tenantId = effectiveTenant;
      const shouldExcludeNorthAmericaForEvasysGovernance =
        isEvasysFamilyTenant(tenantId) && playbookKeyClean === 'evasys_governance_accreditation_ie_v2';

      const withDerivedEnrollmentSignals = (rows) => {
        // Preserve null for missing fields so per-row enrichment can detect gaps.
        const numOrNull = (v) => {
          if (v === null || v === undefined || v === '') return null;
          const n = Number(v);
          return Number.isFinite(n) ? n : null;
        };
        return (rows || []).map((a) => {
          const remaining =
            numOrNull(a?.eligible_remaining) ??
            numOrNull(a?.eligible_untouched) ??
            numOrNull(a?.untouched_count) ??
            null;
          const total =
            numOrNull(a?.eligible_total) ??
            numOrNull(a?.total_eligible) ??
            numOrNull(a?.eligible_personas_count) ??
            null;
          const enrolledElsewhere =
            numOrNull(a?.eligible_enrolled_elsewhere) ??
            numOrNull(a?.enrolled_elsewhere_count) ??
            numOrNull(a?.already_enrolled_count) ??
            numOrNull(a?.enrolled_count) ??
            null;
          let allEnrolledElsewhere = null;
          if (a?.all_eligible_enrolled_elsewhere === true) {
            allEnrolledElsewhere = true;
          } else if (total !== null && total > 0 && remaining !== null && remaining <= 0) {
            allEnrolledElsewhere = true;
          } else if (total !== null && total > 0 && enrolledElsewhere !== null && enrolledElsewhere >= total) {
            allEnrolledElsewhere = true;
          }
          return {
            ...a,
            eligible_remaining: remaining,
            eligible_total: total,
            enrolled_elsewhere_count: enrolledElsewhere,
            all_eligible_enrolled_elsewhere: allEnrolledElsewhere,
          };
        });
      };

      const enrichByEligibleContactsModes = async (accountsToProbe) => {
        const CONCURRENCY = 8;
        const queue = [...(accountsToProbe || [])];
        const enriched = [];

        const worker = async () => {
          while (queue.length) {
            const account = queue.shift();
            if (!account) break;
            const accountId = account.account_id || account.id;
            if (!accountId) {
              enriched.push(account);
              continue;
            }
            try {
              const base = `${API_BASE_URL}/api/playbooks/${playbookKeyClean}/eligible-contacts?tenant_id=${tenantId}&account_id=${encodeURIComponent(accountId)}&user_id=${encodeURIComponent(contextUser.id)}`;
              const [allRes, untouchedRes] = await Promise.all([
                fetch(`${base}&mode=all`, { credentials: 'include' }),
                fetch(`${base}&mode=untouched`, { credentials: 'include' }),
              ]);
              const allData = allRes.ok ? await allRes.json() : {};
              const untouchedData = untouchedRes.ok ? await untouchedRes.json() : {};
              const allCount = Array.isArray(allData?.contacts) ? allData.contacts.length : 0;
              const untouchedCount = Array.isArray(untouchedData?.contacts) ? untouchedData.contacts.length : 0;
              const enrolledElsewhere = Math.max(0, allCount - untouchedCount);
              enriched.push({
                ...account,
                eligible_total: allCount,
                eligible_remaining: untouchedCount,
                enrolled_elsewhere_count: enrolledElsewhere,
                all_eligible_enrolled_elsewhere: allCount > 0 && untouchedCount === 0,
              });
            } catch {
              enriched.push(account);
            }
          }
        };

        await Promise.all(Array.from({ length: CONCURRENCY }, () => worker()));
        return enriched;
      };

      // Try eligible-accounts API first (all tenants) — driven by eligible_remaining
      const headers = { credentials: 'include' };
      if (isEvasysFamilyTenant(tenantId)) headers.headers = { 'X-Tenant-ID': tenantId };
      if (tenantId === 'learnwise') headers.headers = { 'X-Tenant-ID': 'learnwise' };
      const mode = includeAllEnrollmentStates ? 'all' : (showEnrolled ? 'enrolled' : 'untouched');
      const modeParam = `&mode=${encodeURIComponent(mode)}`;
      const res = await fetch(
        `${API_BASE_URL}/api/playbooks/eligible-accounts?tenant_id=${tenantId}&playbook_key=${encodeURIComponent(playbookKeyClean)}&user_id=${encodeURIComponent(contextUser.id)}${modeParam}`,
        headers
      );
      if (res.ok) {
        const data = await res.json();
        const rawAccountsFromApi = data.accounts || [];
        const backendEligibleAccountsListEmpty = rawAccountsFromApi.length === 0;
        console.log('[FE_ELIGIBLE_ACCOUNTS_RAW]', {
          playbookKey: playbookKeyClean,
          tenantId,
          count: rawAccountsFromApi.length || 0,
          accounts: rawAccountsFromApi
        });
        let eligibleAccounts = rawAccountsFromApi
          .map((a) => applyTenantOverrides(a, tenantId))
          .filter((a) => isVisibleForPlaybooks(a, tenantId));

        // Backend already applies mode=untouched / mode=enrolled.
        // Keep only normalization here, no extra enrolled/untouched filtering.
        eligibleAccounts = withDerivedEnrollmentSignals(eligibleAccounts);
        if (isEvasysFamilyTenant(tenantId) && playbookKeyClean === 'evaexam_integrity_assessment_security_v2') {
          eligibleAccounts = eligibleAccounts.filter((a) =>
            isEvasysAccountEligibleForExamIntegrityPlaybook(a.target_products)
          );
        }
        if (isEvasysFamilyTenant(tenantId) && playbookKeyClean === 'evasys_governance_accreditation_ie_v2') {
          eligibleAccounts = eligibleAccounts.filter((a) =>
            isEvasysAccountEligibleForSysGovernancePlaybook(a.target_products)
          );
        }
        if (shouldExcludeNorthAmericaForEvasysGovernance) {
          eligibleAccounts = eligibleAccounts.filter((a) => {
            const country = String(a.country || a.country_name || '').trim().toLowerCase();
            return (
              country !== 'united states' &&
              country !== 'usa' &&
              country !== 'us' &&
              country !== 'canada'
            );
          });
        }

        // In default modes, backend already applies untouched/enrolled semantics.
        // For "all enrollment states", keep full list so FE checkbox can hide fully-enrolled accounts.
        if (!includeAllEnrollmentStates) {
          // Backend already filtered untouched/enrolled modes; keep rows with null remaining.
          eligibleAccounts = eligibleAccounts.filter((a) => {
            const r = a?.eligible_remaining;
            return r === null || r === undefined ? true : Number(r) > 0;
          });
        } else {
          // Per-row backfill: enrich rows missing total/remaining counts.
          const needsEnrichment = (a) => {
            if (a?.eligible_total == null || a?.eligible_remaining == null) return true;
            if (Number(a.eligible_total) === 0 && Number(a.eligible_remaining) === 0) return true;
            return false;
          };
          const rowsToEnrich = eligibleAccounts.filter(needsEnrichment);
          if (rowsToEnrich.length > 0) {
            console.log('[Playbooks] enriching rows with missing counts:', {
              total: eligibleAccounts.length,
              enriching: rowsToEnrich.length,
            });
            const enriched = await enrichByEligibleContactsModes(rowsToEnrich);
            const enrichedById = new Map(
              enriched.map((a) => [a.account_id || a.id, a])
            );
            eligibleAccounts = eligibleAccounts.map(
              (a) => enrichedById.get(a.account_id || a.id) || a
            );
          }
        }

        console.log('[FE_ELIGIBLE_ACCOUNTS_AFTER_FILTER]', {
          playbookKey: playbookKeyClean,
          count: eligibleAccounts?.length || 0,
          accounts: eligibleAccounts || []
        });
        return {
          accounts: sortAccountsByFitScoreDesc(eligibleAccounts),
          backendEligibleAccountsListEmpty,
        };
      }

      // Learnwise fallback: when API returns 404 (endpoint may not exist yet), use AccountFinal
      if (tenantId === 'learnwise' && !res.ok) {
        console.warn('Learnwise eligible-accounts API unavailable, using AccountFinal fallback without fake counts');

        const tenantFilter = { $in: ['learnwise', 'global'] };
        const allAccounts = await AccountFinal.filter(
          { tenant_id: tenantFilter },
          '-account_fit_score',
          200
        );

        let accounts = allAccounts
          .map((a) => applyTenantOverrides(a, tenantId))
          .filter((a) => isVisibleForPlaybooks(a, tenantId));

        accounts = accounts.map(a => ({
          ...a,
          eligible_remaining: null,
          eligible_total: null,
          eligible_count_unknown: true
        }));

        if (showEnrolled) {
          try {
            const statusRes = await fetch(
              `${API_BASE_URL}/api/accounts/batch-playbook-status?tenant_id=learnwise&user_id=${encodeURIComponent(contextUser.id)}`,
              {
                credentials: 'include',
                headers: { 'X-Tenant-ID': 'learnwise' }
              }
            );

            if (statusRes.ok) {
              const statusByName = await statusRes.json();
              const enrolledNames = new Set(
                Object.keys(statusByName).filter(name => statusByName[name])
              );
              accounts = accounts.filter(a => enrolledNames.has(a.account_name));
            } else {
              accounts = [];
            }
          } catch (_) {
            accounts = [];
          }
        }

        return { accounts: sortAccountsByFitScoreDesc(accounts), backendEligibleAccountsListEmpty: false };
      }

      // Evasys fallback (no API fallback)
      if (isEvasysFamilyTenant(tenantId)) {
        console.warn('Evasys eligible-accounts failed:', res.status);
        return emptyFetchResult;
      }

      // For enrichment playbooks (es_*), use the old logic
      if (playbookKeyClean.startsWith('es_')) {
        const allAccounts = await AccountFinal.filter({ tenant_id: tenantId }, '-account_fit_score', 200);
        let accountsWithEligiblePeople = allAccounts
          .map((account) => applyTenantOverrides(account, tenantId))
          .filter((account) => isVisibleForPlaybooks(account, tenantId));
        
        // Handle enrolled filtering
        const statusResponse = await fetch(
          `${API_BASE_URL}/api/accounts/batch-playbook-status?tenant_id=${tenantId}&user_id=${encodeURIComponent(contextUser.id)}`,
          { credentials: 'include' }
        );
        
        let enrolledAccountNames = new Set();
        if (statusResponse.ok) {
          const statusByName = await statusResponse.json();
          enrolledAccountNames = new Set(Object.keys(statusByName).filter(name => statusByName[name]));
        }

        if (showEnrolled) {
          accountsWithEligiblePeople = accountsWithEligiblePeople.filter(account =>
            enrolledAccountNames.has(account.account_name)
          );
        } else {
          accountsWithEligiblePeople = accountsWithEligiblePeople.filter(account =>
            !enrolledAccountNames.has(account.account_name)
          );
        }

        console.log('📊 Enrichment playbook eligible accounts:', accountsWithEligiblePeople.length);
        return {
          accounts: sortAccountsByFitScoreDesc(accountsWithEligiblePeople),
          backendEligibleAccountsListEmpty: false,
        };
      }
      
      // For all other playbooks, use eligible-contacts (DB-driven) per account
      const allAccounts = await AccountFinal.filter({ tenant_id: tenantId }, '-account_fit_score', 200);
      const visibleAccounts = allAccounts
        .map((account) => applyTenantOverrides(account, tenantId))
        .filter((account) => isVisibleForPlaybooks(account, tenantId));

      // Call eligible-contacts for each account (concurrency limit 10)
      const CONCURRENCY = 10;
      const checkAccount = async (account) => {
        const accountId = account.account_id || account.id;
        try {
          const modeForContacts = mode || 'untouched';
          const res = await fetch(
            `${API_BASE_URL}/api/playbooks/${playbookKeyClean}/eligible-contacts?tenant_id=${tenantId}&account_id=${encodeURIComponent(accountId)}&user_id=${encodeURIComponent(contextUser.id)}&mode=${encodeURIComponent(modeForContacts)}`,
            { credentials: 'include' }
          );
          if (!res.ok) return null;
          const data = await res.json();
          const contacts = data.contacts || [];
          if (contacts.length === 0) return null;
          return { ...account, eligible_remaining: contacts.length, eligible_total: contacts.length };
        } catch {
          return null;
        }
      };

      const chunks = [];
      for (let i = 0; i < visibleAccounts.length; i += CONCURRENCY) {
        chunks.push(visibleAccounts.slice(i, i + CONCURRENCY));
      }
      const results = await Promise.all(
        chunks.map(chunk => Promise.all(chunk.map(checkAccount)))
      );
      let eligibleAccounts = results.flat().filter(Boolean);
      if (shouldExcludeNorthAmericaForEvasysGovernance) {
        eligibleAccounts = eligibleAccounts.filter((a) => {
          const country = String(a.country || a.country_name || '').trim().toLowerCase();
          return (
            country !== 'united states' &&
            country !== 'usa' &&
            country !== 'us' &&
            country !== 'canada'
          );
        });
      }
      if (isEvasysFamilyTenant(tenantId) && playbookKeyClean === 'evaexam_integrity_assessment_security_v2') {
        eligibleAccounts = eligibleAccounts.filter((a) =>
          isEvasysAccountEligibleForExamIntegrityPlaybook(a.target_products)
        );
      }
      if (isEvasysFamilyTenant(tenantId) && playbookKeyClean === 'evasys_governance_accreditation_ie_v2') {
        eligibleAccounts = eligibleAccounts.filter((a) =>
          isEvasysAccountEligibleForSysGovernancePlaybook(a.target_products)
        );
      }
      // Fallback has no enrollment info — show all (remaining === total from contacts.length)
      console.log('📊 eligible-contacts fallback:', {
        playbookKey: playbookKeyClean,
        count: eligibleAccounts.length,
        showingEnrolled: showEnrolled
      });

      return { accounts: sortAccountsByFitScoreDesc(eligibleAccounts), backendEligibleAccountsListEmpty: false };
    } catch (error) {
      console.error('Error fetching eligible accounts:', error);
      return emptyFetchResult;
    }
  };

  const toggleShowEnrolled = (playbookKeyOrId) => {
    const key = normalizePlaybookKey(playbookKeyOrId);
    setShowEnrolledMap(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handlePreviewClick = async (playbook) => {
    console.log('🎯 Playbooks: handlePreviewClick called with:', playbook);
    const pk = getPlaybookKey(playbook);
    const rawPlaybookKey = getRawPlaybookKey(playbook);
    const isEventCustomPlaybook =
      playbook?.is_event_custom === true || rawPlaybookKey.toLowerCase().startsWith('event::');

    // Event playbooks route to EventPlaybookPreview:
    // - canonical global event playbooks (EVENT_PLAYBOOK_KEYS)
    // - synthetic custom event playbooks (event::<uuid>)
    if (EVENT_PLAYBOOK_KEYS.includes(pk) || isEventCustomPlaybook) {
      const eventIdForPreview = playbook?.event_id || selectedEventId;
      const playbookKeyForPreview = isEventCustomPlaybook ? rawPlaybookKey : pk;
      if (!eventIdForPreview) {
        toast.error('No event selected', {
          description: 'Please select an event first or add events in Settings'
        });
        return;
      }
      const eventForGate = upcomingEvents.find(
        (e) => (e.id || e.event_name) === eventIdForPreview,
      );
      if (isSessionInvitePlaybookKey(pk) && !hasEventSessionDetails(eventForGate)) {
        toast.error(t('sessionInviteBlockedTitle') || 'Session details required', {
          description: getEligibilityBlockMessage('missing_session_details'),
        });
        return;
      }
      navigate(
        `${createPageUrl('EventPlaybookPreview')}?event_id=${encodeURIComponent(eventIdForPreview)}&playbook_key=${encodeURIComponent(playbookKeyForPreview)}`
      );
      return;
    }

    setIsFetchingAccounts(true);
    setSelectedPlaybook(playbook);
    setHideFullyEnrolledAccounts(true);
    setAccountCountryFilter('all');

    try {
      const showEnrolled = showEnrolledMap[pk] || false;
      // Checkbox (hideFullyEnrolledAccounts) relies on cross-playbook enrollment signals.
      // Keep "enrolled" strict, but use "all enrollment states" in untouched view so
      // the preselected checkbox can filter accounts that have no persona untouched-anywhere.
      const { accounts: eligibleData, backendEligibleAccountsListEmpty } = await fetchEligibleAccounts(
        pk,
        showEnrolled,
        !showEnrolled
      );
      
      if (eligibleData.length === 0) {
        if (backendEligibleAccountsListEmpty) {
          toast.error('No eligible accounts', {
            description:
              `No execution-eligible accounts were returned by the backend for "${getPlaybookDisplayName(playbook)}". This may be due to account-tier normalization in channel eligibility.`,
          });
        } else {
          const message = showEnrolled
            ? `No enrolled accounts found for "${getPlaybookDisplayName(playbook)}"`
            : `No untouched accounts found for "${getPlaybookDisplayName(playbook)}"`;

          toast.error('No eligible accounts', {
            description: message
          });
        }
        setIsFetchingAccounts(false);
        return;
      }
      
      // BE's /eligible-accounts?mode=all already returns cross-playbook enrollment counts.
      // Keep FE filtering purely client-side using those fields.
      setFilteredAccounts(eligibleData);
      setShowAccountSelector(true);
    } catch (error) {
      console.error('Error in handlePreviewClick:', error);
      toast.error('Error loading accounts', {
        description: 'Please try again'
      });
    } finally {
      setIsFetchingAccounts(false);
    }
  };

  const handleAccountSelected = (account) => {
    console.log('🎯 Playbooks: handleAccountSelected called');
    console.log('  - Selected account:', account);
    console.log('  - Selected playbook:', selectedPlaybook);

    const accountId = account?.account_id || account?.id;
    
     if (!account || !accountId) {
      console.error('❌ No account or account_id');
      return;
    }
    
    const playbookKey = getPlaybookKey(selectedPlaybook);
    if (!selectedPlaybook || !playbookKey) {
      console.error('❌ No playbook or playbook key');
      return;
    }
    
    const showEnrolled = showEnrolledMap[playbookKey] || false;
    const mode = showEnrolled ? 'enrolled' : 'untouched';

    const debugId = `pb_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    console.log('[PLAYBOOK_CLICK]', {
      debugId,
      accountId,
      playbookKey,
      mode,
      currentTenant,
      adminTenant: contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null
    });

    // Construct URL with parameters (use clean playbook key for API)
    const baseUrl = createPageUrl('PlaybookPreview');
    const params = new URLSearchParams({
      account_id: accountId,
      playbook_key: playbookKey,
      debug_id: debugId,
      mode
    });
    const fullUrl = `${baseUrl}?${params.toString()}`;
    
    console.log('🎯 Navigating to:', fullUrl);
    console.log('  - Base URL:', baseUrl);
    console.log('  - Params:', params.toString());
    
    // Close account selector
    setShowAccountSelector(false);
    
    // Navigate
    navigate(fullUrl);
  };

  const handleToggleHideFullyEnrolledAccounts = (checked) => {
    console.log('[Playbooks] hideFullyEnrolledAccounts toggle:', {
      checked,
      filteredAccountsCount: (filteredAccounts || []).length,
    });
    setHideFullyEnrolledAccounts(checked);
  };

  const accountsForSelector = React.useMemo(() => {
    if (!Array.isArray(filteredAccounts)) return [];

    const num = (v) => {
      if (v === null || v === undefined || v === '') return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const hasAnyUntouched = (a) => {
      if (a?.all_eligible_enrolled_elsewhere === true) return false;

      const remaining =
        num(a?.eligible_remaining) ??
        num(a?.eligible_untouched) ??
        num(a?.untouched_count) ??
        null;
      const total =
        num(a?.eligible_total) ??
        num(a?.total_eligible) ??
        num(a?.eligible_personas_count) ??
        null;
      const enrolledElsewhere =
        num(a?.enrolled_elsewhere_count) ??
        num(a?.eligible_enrolled_elsewhere) ??
        num(a?.already_enrolled_count) ??
        num(a?.enrolled_count) ??
        null;

      if (remaining !== null) return remaining > 0;
      if (total !== null && enrolledElsewhere !== null) {
        return total - enrolledElsewhere > 0;
      }
      return true;
    };

    const enrollmentFiltered = hideFullyEnrolledAccounts
      ? filteredAccounts.filter(hasAnyUntouched)
      : filteredAccounts;
    const filtered = accountCountryFilter === 'all'
      ? enrollmentFiltered
      : enrollmentFiltered.filter((a) => {
          const c = String(a?.country || a?.country_name || '').trim();
          return c === accountCountryFilter;
        });
    const hiddenSample = hideFullyEnrolledAccounts
      ? filteredAccounts
          .filter((a) => !enrollmentFiltered.includes(a))
          .slice(0, 5)
          .map((a) => ({
            account_id: a?.account_id || a?.id,
            account_name: a?.account_name,
            eligible_total: a?.eligible_total,
            eligible_remaining: a?.eligible_remaining,
            all_eligible_enrolled_elsewhere: a?.all_eligible_enrolled_elsewhere,
          }))
      : [];

    console.log('[Playbooks] accountsForSelector recompute:', {
      hideFullyEnrolledAccounts,
      inputCount: filteredAccounts.length,
      outputCount: filtered.length,
      sample: filtered.slice(0, 5).map((a) => ({
        account_id: a?.account_id || a?.id,
        account_name: a?.account_name,
        eligible_total: a?.eligible_total,
        eligible_remaining: a?.eligible_remaining,
        all_eligible_enrolled_elsewhere: a?.all_eligible_enrolled_elsewhere,
      })),
      hiddenSample,
    });

    return filtered;
  }, [filteredAccounts, hideFullyEnrolledAccounts, accountCountryFilter]);

  const accountCountryOptions = React.useMemo(() => {
    return Array.from(
      new Set(
        (filteredAccounts || [])
          .map((a) => String(a?.country || a?.country_name || '').trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [filteredAccounts]);

  const eventPlaybooks = playbooks.filter((p) => EVENT_PLAYBOOK_KEYS.includes(getEventPlaybookKey(p)));
  const regularPlaybooks = playbooks.filter(p => !EVENT_PLAYBOOK_KEYS.includes(getPlaybookKey(p)));
  // DIY only: custom event sequences belong in Settings > Events (not duplicate tiles + "Manage in Events" here).
  const combinedCustomPlaybooks = [...diyPlaybooks].filter(Boolean);

  const filteredPlaybooks = regularPlaybooks.filter(playbook => {
    const matchesSearch = searchTerm === '' || 
      playbook.playbook_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      playbook.goal?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFactor = filterFactor === 'all' || playbook.factor === filterFactor;
    
    return matchesSearch && matchesFactor;
  });

  const getDaysUntilEvent = (startDate) => {
    if (!startDate) return null;
    const today = new Date();
    const eventDate = new Date(startDate);
    const diffTime = eventDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getEventPlaybookIcon = (playbookId) => {
    const display = getEventPlaybookDisplay(playbookId);
    const iconName = display?.icon;
    const icons = {
      MapPin,
      Sparkles,
      Star,
      ArrowUpCircle,
      event_booth_meeting_v1: MapPin,
      event_session_invite_v1: Sparkles,
      event_vip_outreach_v1: Star,
      event_quick_connect_v1: ArrowUpCircle,
    };
    if (iconName && icons[iconName]) return icons[iconName];
    return icons[playbookId] || CalendarDays;
  };

  const selectedEvent = upcomingEvents.find(
    (e) => (e.id || e.event_name) === selectedEventId,
  );
  const selectedEventConfirmedCount = getEventConfirmedAttendeeCount(selectedEvent);

  const handleCloneToEdit = async (playbook, cloneSource = 'playbooks_list') => {
    const playbookId = playbook?.id || playbook?.playbook_id;
    const tenantId = tenantIdForDiy;
    const uid = diyPlaybookEditorUserId(contextUser);
    const owned = isDiyPlaybookOwnedByUser(playbook, contextUser);

    const result = await cloneDiyPlaybook({
      playbookId,
      tenantId,
      userId: uid,
      cloneSource,
      playbookName: playbook?.name ?? null,
      sourceOwnerUserId: diyPlaybookOwnerUserId(playbook),
    });

    if (!result.ok) {
      if (result.reason === 'missing_context') {
        toast.error('Missing tenant or user context');
      } else if (result.reason === 'missing_new_id') {
        toast.error('Clone succeeded but no playbook id was returned.');
      } else {
        toast.error('Failed to clone playbook. Try again.');
      }
      return;
    }

    navigate(`${createPageUrl('DIYPlaybook')}?id=${encodeURIComponent(result.newPlaybookId)}`);
    toast.success(
      owned ? 'Playbook cloned. Opening copy…' : 'Cloned to your account. You can now edit and save.',
    );
  };

  const handleDeleteDiyPlaybook = async () => {
    if (!deleteTarget) return;
    
    setIsDeleting(true);
    try {
      const tenantId = tenantIdForDiy || String(deleteTarget?.tenant_id || '').trim().toLowerCase();
      const playbookId = deleteTarget.id || deleteTarget.playbook_id;

      // Try API first
      try {
        await fetch(`${API_BASE_URL}/api/diy-playbooks/${playbookId}?tenant_id=${tenantId}`, {
          method: 'DELETE',
          credentials: 'include'
        });
      } catch {
        // Ignore API errors
      }

      // Delete from local storage
      const localKey = `diy-playbooks-${tenantId}`;
      try {
        const stored = localStorage.getItem(localKey);
        const localPlaybooks = stored ? JSON.parse(stored) : [];
        const filtered = localPlaybooks.filter(p => p.id !== playbookId);
        localStorage.setItem(localKey, JSON.stringify(filtered));
      } catch {
        // Ignore
      }

      // Update state
      setDiyPlaybooks(prev => prev.filter(p => (p.id || p.playbook_id) !== playbookId));
      toast.success('Playbook deleted');
    } catch (error) {
      console.error('Error deleting playbook:', error);
      toast.error('Failed to delete playbook');
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  const getFactorIcon = (factor) => {
    const icons = {
      'Market Conditions': TrendingUp,
      'Account Signals': Zap,
      'Persona Activity': Users,
      'Strategic Timing': Target
    };
    return icons[factor] || Target;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00C2C2]"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 -m-8 -mt-8 min-h-screen">
      <div className="bg-white border-b mb-6 -mx-8">
        <div className="max-w-7xl mx-auto px-12 sm:px-8 md:px-10 py-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-[#0A1A2F]">{t('playbooks')}</h1>
              <p className="text-lg text-[#5C6B7A]">{t('structuredEngagement')}</p>
            </div>
            <Button
              onClick={() => navigate(createPageUrl('DIYPlaybook'))}
              className="bg-[#00C2C2] hover:bg-[#00A3A3]"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('createPlaybook')}
            </Button>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-2 sm:px-6 py-0">
      <div className="space-y-6">

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder={t('searchPlaybooks')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterFactor} onValueChange={setFilterFactor}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder={t('filterByFactor')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('allFactors')}</SelectItem>
              <SelectItem value="Market Conditions">Market Conditions</SelectItem>
              <SelectItem value="Account Signals">Account Signals</SelectItem>
              <SelectItem value="Persona Activity">Persona Activity</SelectItem>
              <SelectItem value="Strategic Timing">Strategic Timing</SelectItem>
            </SelectContent>
          </Select>
        </div>

      {/* Event Playbooks Section */}
      {eventPlaybooks.length > 0 && (
        <>
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-[#0A1A2F] mb-2 flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#00C2C2]" />
              {t('eventPlaybooks')}
            </h3>
            <p className="text-sm text-[#5C6B7A] mb-4">{t('targetedSequences')}</p>
            
            {upcomingEvents.length === 0 ? (
              <Card className="border-0 shadow-md bg-blue-50">
                <CardContent className="p-6 text-center">
                  <CalendarDays className="w-12 h-12 mx-auto mb-3 text-blue-400" />
                  <p className="text-sm text-[#5C6B7A] mb-2">{t('noUpcomingEvents')}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(createPageUrl('SettingsEvents'))}
                  >
                    {t('addEventsInSettings')}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="mb-4">
                  <Label className="text-sm font-medium mb-2 block">{t('selectEvent')}</Label>
                  <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                    <SelectTrigger className="w-full max-w-md">
                      <SelectValue placeholder="Choose an event" />
                    </SelectTrigger>
                    <SelectContent>
                      {upcomingEvents.map(event => {
                        const daysAway = getDaysUntilEvent(event.start_date);
                        return (
                          <SelectItem key={event.id || event.event_name} value={event.id || event.event_name}>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{event.event_name || event.name}</span>
                              <span className="text-xs text-gray-500">
                                • {event.city} • {daysAway > 0 ? `${daysAway} days away` : 'Today'}
                              </span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {selectedEventId &&
                  !hasEventSessionDetails(selectedEvent) &&
                  eventPlaybooks.some((p) => isSessionInvitePlaybookKey(getEventPlaybookKey(p))) && (
                  <Alert className="mb-4 border-amber-200 bg-amber-50">
                    <AlertTitle className="text-amber-900 text-sm font-semibold">
                      {t('sessionInviteBlockedTitle') || 'Session details required for Session Invite'}
                    </AlertTitle>
                    <AlertDescription className="text-amber-800 text-sm mt-1 space-y-2">
                      <p>
                        {t('sessionInviteEventBanner') ||
                          'Session Invite needs a session title and description for the selected event. Open Settings → Events, edit this event, and fill in Session Details.'}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-amber-300 text-amber-900 hover:bg-amber-100"
                        onClick={() => navigate(createPageUrl('SettingsEvents'))}
                      >
                        {t('openEventsSettings') || 'Open Events settings'}
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
                  {eventPlaybooks.map((playbook) => {
                    const pk = getEventPlaybookKey(playbook);
                    const display = getEventPlaybookDisplay(pk);
                    const Icon = getEventPlaybookIcon(pk);
                    const isQuickConnect = isQuickConnectPlaybookKey(pk);
                    const isSessionInvite = isSessionInvitePlaybookKey(pk);
                    const previewBlocked = isEventPlaybookPreviewBlocked(
                      pk,
                      selectedEventId,
                      selectedEvent,
                    );
                    const previewDisabled = previewBlocked;
                    const purposeLine =
                      (display?.purpose != null && display?.purpose_key
                        ? t(display.purpose_key)
                        : display?.purpose) ??
                      playbook.goal ??
                      playbook.recommended_cadence ??
                      '';

                    const previewButton = (
                      <Button
                        onClick={() => handlePreviewClick(playbook)}
                        disabled={previewDisabled}
                        className="w-full bg-[#00C2C2] hover:bg-[#00A3A3] text-sm py-2 disabled:opacity-50"
                        size="sm"
                      >
                        <Play className="w-3 h-3 mr-1" />
                        Preview & Apply
                      </Button>
                    );

                    return (
                      <Card
                        key={playbook.id}
                        className="border-0 shadow-md hover:shadow-lg transition-shadow flex flex-col h-full"
                      >
                        <CardHeader className="pb-3 flex-none">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex items-start gap-2 min-w-0">
                              <Icon className="w-5 h-5 text-[#00C2C2] flex-shrink-0 mt-0.5" />
                              <CardTitle className="text-base text-[#0A1A2F] leading-tight">
                                {display?.label ?? getPlaybookDisplayName(playbook)}
                              </CardTitle>
                            </div>
                            {display?.persona_scope && (
                              <Badge
                                variant="outline"
                                className={`text-[10px] shrink-0 ${display.persona_scope_color}`}
                              >
                                {display.persona_scope_key ? t(display.persona_scope_key) : display.persona_scope}
                              </Badge>
                            )}
                          </div>
                          <CardDescription className="text-xs text-[#5C6B7A] line-clamp-2 min-h-[2.5rem]">
                            {purposeLine}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="flex flex-col flex-1 gap-3 pt-0">
                          {selectedEventId ? (
                            <EventCustomPlaybookStatusBanner
                              event={selectedEvent}
                              playbookKey={pk}
                              compact
                              onlyIfCustomActive
                            />
                          ) : (
                            <p className="text-[10px] text-[#5C6B7A] min-h-[2.5rem] leading-snug">
                              Select an event to see custom playbook status
                            </p>
                          )}
                          {isQuickConnect && selectedEventId && (
                            <p className="text-[10px] text-[#5C6B7A] leading-snug">
                              {selectedEventConfirmedCount > 0
                                ? `${selectedEventConfirmedCount} confirmed attendee${selectedEventConfirmedCount === 1 ? '' : 's'} at this event`
                                : t('quickConnectRequiresAttendees') ||
                                  'Requires at least one confirmed attendee'}
                            </p>
                          )}
                          {isSessionInvite && selectedEventId && (
                            <p className="text-[10px] text-[#5C6B7A] leading-snug">
                              {hasEventSessionDetails(selectedEvent)
                                ? t('sessionInviteReady') ||
                                  'Session title and description configured'
                                : t('sessionInviteRequiresSessionDetails') ||
                                  'Requires session title and description in Settings → Events'}
                            </p>
                          )}
                          <div className="mt-auto">
                          {previewBlocked && selectedEventId ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="block w-full">{previewButton}</span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs text-xs">
                                  {isQuickConnect
                                    ? getEligibilityBlockMessage('no_confirmed_attendees')
                                    : isSessionInvite
                                      ? getEligibilityBlockMessage('missing_session_details')
                                      : getEligibilityBlockMessage('')}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            previewButton
                          )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* DIY Playbooks Section */}
      {combinedCustomPlaybooks.length > 0 && (
        <>
          <h3 className="text-lg font-semibold text-[#0A1A2F] mt-8">{t('yourCustomPlaybooks')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {combinedCustomPlaybooks.filter(p => {
              const matchesSearch = searchTerm === '' || 
                p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                p.goalDescription?.toLowerCase().includes(searchTerm.toLowerCase());
              return matchesSearch;
            }).map((playbook) => (
              <Card key={playbook.id || playbook.playbook_id} className="border-0 shadow-md hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                      {playbook.source === 'event_custom' ? 'Event Custom' : 'Custom'}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg text-[#0A1A2F]">
                    {playbook.name}
                  </CardTitle>
                  <CardDescription className="text-sm text-[#5C6B7A] line-clamp-2">
                    {playbook.goalDescription}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {playbook.sectorMode === 'all' && <Badge variant="secondary">{t('allSectors')}</Badge>}
                    {playbook.sectorMode === 'single' && playbook.sector && <Badge variant="secondary">{playbook.sector}</Badge>}
                    {playbook.sectorMode === 'multiple' && playbook.sectors?.length > 0 && (
                      <Badge variant="secondary">{playbook.sectors.length} Sectors</Badge>
                    )}
                    {playbook.source === 'event_custom' && playbook.touches_count > 0 && (
                      <Badge variant="outline">{playbook.touches_count} Touches</Badge>
                    )}
                    {playbook.source !== 'event_custom' && playbook.steps?.length > 0 && (
                      <Badge variant="outline">{playbook.steps.length} {t('steps')}</Badge>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {playbook.source === 'event_custom' ? (
                      <Button
                        variant="outline"
                        onClick={() => navigate(createPageUrl('SettingsEvents'))}
                        className="flex-1"
                      >
                        <CalendarDays className="w-4 h-4 mr-2" />
                        Manage in Events
                      </Button>
                    ) : (
                      <>
                        {isDiyPlaybookOwnedByUser(playbook, contextUser) ? (
                          <Button
                            variant="outline"
                            onClick={() => {
                              const pid = playbook.id || playbook.playbook_id;
                              navigate(`${createPageUrl('DIYPlaybook')}?id=${encodeURIComponent(pid)}`);
                            }}
                            className="flex-1 min-w-[100px]"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            {t('edit')}
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            onClick={() => handleCloneToEdit(playbook, 'playbooks_clone_to_edit')}
                            className="flex-1 min-w-[100px]"
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Clone to edit
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          onClick={() => setDeleteTarget(playbook)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Standard Playbooks Grid */}
      {filteredPlaybooks.length > 0 && (
        <>
          <h3 className="text-lg font-semibold text-[#0A1A2F] mt-8">{t('standardPlaybooks')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredPlaybooks.map((playbook) => {
              const FactorIcon = getFactorIcon(playbook.factor);
              
              return (
                <Card key={playbook.id || getPlaybookKey(playbook)} className="border-0 shadow-md hover:shadow-lg transition-shadow flex flex-col h-full">
                  <CardHeader>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <FactorIcon className="w-5 h-5 text-[#00C2C2]" />
                        {playbook.factor && <Badge variant="secondary">{playbook.factor}</Badge>}
                      </div>
                    </div>
                    <CardTitle className="text-lg text-[#0A1A2F]">
                      {getPlaybookDisplayName(playbook)}
                    </CardTitle>
                    <CardDescription className="text-sm text-[#5C6B7A]">
                      {playbook.when_to_use || playbook.goal || ''}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-col flex-1 pt-0">
                    <div className="flex-grow min-h-0">
                      <h4 className="text-sm font-semibold text-[#0A1A2F] mb-2">Goal</h4>
                      <p className="text-sm text-[#5C6B7A]">{playbook.goal}</p>
                    </div>
                    
                    {/* Toggle Switch + Button pinned to bottom */}
                    <div className="mt-auto space-y-4 pt-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-gray-600" />
                        <span className="text-sm text-gray-700">
                          {showEnrolledMap[getPlaybookKey(playbook)] ? 'Enrolled accounts' : 'Untouched accounts'}
                        </span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showEnrolledMap[getPlaybookKey(playbook)] || false}
                          onChange={() => toggleShowEnrolled(getPlaybookKey(playbook))}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#00C2C2]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#00C2C2]"></div>
                      </label>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handlePreviewClick(playbook)}
                        disabled={isFetchingAccounts}
                        className="flex-1 bg-[#00C2C2] hover:bg-[#00A3A3]"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        {isFetchingAccounts ? 'Loading...' : 'Preview & Apply'}
                      </Button>
                    </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {filteredPlaybooks.length === 0 && combinedCustomPlaybooks.length === 0 && (
        <div className="text-center py-12">
          <p className="text-[#5C6B7A]">{t('noPlaybooks')}</p>
        </div>
      )}

      {/* Delete DIY Playbook Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Playbook</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDiyPlaybook}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Account Selector Dialog */}
      {showAccountSelector && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-[#0A1A2F]">
                Select Account for {getPlaybookDisplayName(selectedPlaybook)}
              </h3>
              <p className="text-sm text-[#5C6B7A] mt-1">
                Choose which account to apply this playbook to
              </p>
              <div className="mt-3 flex flex-col md:flex-row md:items-center gap-2 md:gap-3">
                <label className="inline-flex items-center gap-2 text-sm text-[#5C6B7A] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hideFullyEnrolledAccounts}
                    onChange={(e) => handleToggleHideFullyEnrolledAccounts(e.target.checked)}
                  />
                  Show only accounts with at least 1 persona not already enrolled
                </label>
                <Select value={accountCountryFilter} onValueChange={setAccountCountryFilter}>
                  <SelectTrigger className="w-full md:w-[180px] h-9 text-sm">
                    <SelectValue placeholder="All countries" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All countries</SelectItem>
                    {accountCountryOptions.map((country) => (
                      <SelectItem key={country} value={country}>
                        {country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-180px)]">
              <AccountSelector
                onSelect={handleAccountSelected}
                currentTenant={currentTenant}
                eligibleAccounts={accountsForSelector}
                playbookKey={getPlaybookKey(selectedPlaybook)}
              />
            </div>
            <div className="p-4 border-t flex justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  console.log('🎯 Closing account selector');
                  setShowAccountSelector(false);
                  setSelectedPlaybook(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      </div>
      </div>
    </div>
  );
}